import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self, TemplateRef, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NgControl} from '@angular/forms';
import {MatFormFieldControl} from '@angular/material/form-field';
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {TranslocoService} from "@ngneat/transloco";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";
import {IconDefinition} from "@fortawesome/fontawesome-common-types";
import {faArrowsAlt} from "@fortawesome/free-solid-svg-icons";
import {MatPaginator} from '@angular/material/paginator';
import {ToastrService} from "ngx-toastr";
import {ButtonBarComponent, IButtonBar} from "../add-remove-bar/button-bar.component";
import {DialogPopComponent} from "../dialog-pop/dialog-pop.component";
import {Lib} from "../../../libs/Lib";
import { Memoizee } from '../../decorators/memoizee-decorator';
import {Compbaser} from "../compbaser";
import {Subject} from 'rxjs';
import {debounce} from "utils-decorators";
import * as _ from "lodash";

export function instanceOfTableList(object: any): object is ITableListEvent {
    try {
        return 'currentAction' in object;
    } catch (e) {
        return false;
    }
}

class Data {
    constructor(public _data: Array<any>) {
    }
}

interface ITableItemInput {
    __TableId?: any;
    $: {
        [index: string]: string;
    };
}

export interface ITableListEvent {
    selectedIndexes?: Array<number>;
    selected?: Array<any>;
    currentAction: string;
    values?: Array<any>;
    eventField?: string;
}

export interface ITableListColumn {
    name: string;
    label?: string;
    maxStringSize: string;
    inputType: 'edit' | 'text' | 'select' | 'edit-button' | 'fixed' | 'number' | 'color';
    selectValues?: Array<string>;
    selectValue?: string;
}

@Component({
    selector: 'app-table-list-input',
    templateUrl: 'table-list-input.component.html',
    styleUrls: ['./table-list-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{provide: MatFormFieldControl, useExisting: TableListInputComponent, multi: true}],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    },
    animations: [
        trigger('detailExpand', [
            state('collapsed, void', style({height: '0px'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
            // transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ]),
    ],
})
export class TableListInputComponent extends Compbaser implements ControlValueAccessor, OnInit, DoCheck, MatFormFieldControl<any>, OnDestroy {

    static nextId = 0;
    static ngAcceptInputType_disabled: boolean | string | null | undefined;
    static ngAcceptInputType_required: boolean | string | null | undefined;

    @ViewChild(ButtonBarComponent) _buttonBarComponent: ButtonBarComponent;
    @ViewChild('dtable') dtable: any;
    @ViewChild("tableModal", {read: TemplateRef, static: false}) tableModal: TemplateRef<any>;

    @ViewChild(MatPaginator, {static: false}) set paginator(value: MatPaginator) {
        if (this.enablePagination && this.dataSource) {
            this.dataSource.paginator = value;
        }
    }

    @Output() update: EventEmitter<ITableListEvent> = new EventEmitter();
    @Input() confirmRemove = true;
    @Input() multiSelect = true;
    @Input() buttonBarClassName = '';
    @Input() tableClassName = '';
    @Input() callBackEvent: (v, callback: any) => ITableListEvent;
    @Input() callBackValue: (v, direction: 'IN' | 'OUT', index?: number) => ITableItemInput;
    @Input() callBackFaIcon: (e) => IconDefinition;
    @Input() callBackEditIcon: (e) => IconDefinition;
    @Input() disableSubmit = false;
    @Input() addItemTemplate: ITableItemInput;
    @Input() inputEditFormat = {};
    @Input() requiredFields = {};
    @Input() inputEditRenamePath: Array<any>;
    @Input() rememberSelection: boolean;
    @Input() allowFilter;
    @Input() expandableEditor = false;
    @Input() enablePagination = false;
    @Input() keepTableIds = false;
    @Input() cloneVals = false;
    @Input() disableDrag = false;
    @Input() allowTableOnDialog = false;

    @Input()
    set initFilter(i_filter: string) {
        if (i_filter || i_filter === '') {
            this.filter = i_filter;
            this.showFilter = true;
            this.applyFilter(this.filter);
        }
    }

    @Input()
    set buttons(i_button: IButtonBar[]) {
        this._buttons = _.cloneDeep(i_button);
    }

    @Input()
    set tableColumns(i_editableColumns: ITableListColumn[]) {
        this.tableColumn = i_editableColumns;
        this.tableColumnNames = i_editableColumns.map((v) => v.name);
    }

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.formGroup.disable() : this.formGroup.enable();
        this.stateChanges.next();
    }

    @Input()
    get value(): any {
        if (this.formGroup.valid) {
            const {value: {_data}} = this.formGroup;
            // return _data;
            return new Data(_data);
        }
        return null;
    }

    set value(val: any) {
        if (!val || !_.isArray(val)) return;
        if (this.cloneVals) val = _.cloneDeep(val);
        let selected;
        if (this.rememberSelection) selected = this.getSelection();

        let expandedIndex = -1;
        if (this.expandedElement) {
            expandedIndex = this.dataSource.data.indexOf(this.expandedElement);
        }

        this.highlightedRowsCopy = [];
        this.dataSource.data = val.filter((item) => !!item).map((item, index) => {
            item.__TableId = _.uniqueId();
            if (this.callBackValue)
                item = this.callBackValue(item, 'IN', index);

            if (expandedIndex === index) {
                this.expandedElement = item;
            }
            return item;
        });

        if (this.rememberSelection) this.setSelection(selected);
        this.applyFilterAbility();
        this.applyFilter(this.filter);

        this.cd.markForCheck();
    }

    _buttons: IButtonBar[] = [];
    _placeholder: string;
    _disabled = false;
    _required = false;
    dataSource = new MatTableDataSource<ITableItemInput>([]);
    highlightedRows: Array<ITableItemInput> = [];
    highlightedRowsCopy: Array<ITableItemInput> = [];
    formGroup: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    errorState = false;
    controlType = 'example-tel-input';
    id = `example-tel-input-${TableListInputComponent.nextId++}`;
    describedBy = '';
    tableColumn: Array<ITableListColumn> = [];
    tableColumnNames: Array<string> = [];
    onChange;
    onTouched;
    showFilter = false;
    filter;
    style;
    draggingNow = false;
    faArrowsAlt = faArrowsAlt;
    expandedElement;
    pageSize = 10;
    pageSizeOptions = [5, 10, 20, 30];
    editable = true;
    isTableOnDialog = false;
    dialogRef: MatDialogRef<any, any>;

    constructor(formBuilder: FormBuilder, private _focusMonitor: FocusMonitor,
                public override dialog: MatDialog, private translocoService: TranslocoService,
                private cd: ChangeDetectorRef, public _elementRef: ElementRef<HTMLElement>,
                private toastr: ToastrService,
                @Optional() @Self() public ngControl: NgControl) {

        super(dialog);
        this.formGroup = formBuilder.group({
            _data: [null]
            // _data: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(3)]]
        });
        _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                // added 10-10-2022 try and fix log error
                if (this.onTouched)
                    this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        // this.ngControl = this.injector.get(NgControl);
        if (this.ngControl != null) {
            this._announceChange('init');
            this.ngControl.valueAccessor = this;
        }
        if (this.expandableEditor && this.allowFilter) {
            this._buttons.push(
                {
                    callBack: v => this.toggleFilter(),
                    icon: 'search',
                    name: 'search',
                    disableByList: false,
                    disabled: false,
                    tooltip: this.translocoService.translate('general.search', {}, this.translocoService.getActiveLang())
                }
            );
        }
        if (this.allowTableOnDialog) {
            this._buttons.push(
                {
                    callBack: v => this.showTableOnDialog(),
                    icon: 'grid_on',
                    name: 'tableOnDialog',
                    disableByList: false,
                    disabled: false,
                    hidden: this.isTableOnDialog
                }
            );
        }
        if (this.tableColumn && this.tableColumn.every((col) => col.inputType === 'text' || col.inputType === 'fixed')) {
            this.editable = false;
        }
    }

    override ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    @debounce(333)
    ngDoCheck(): void {
        if (this.ngControl) {
            this.errorState = this.ngControl.invalid && this.ngControl.touched;
            this.stateChanges.next();
        }
    }

    updateField(value, item, col, collapseRow = null) {
        // 4-1-2021 added trim and inability to have blank values
        if (value == null) return;

        if (this.requiredFields && this.requiredFields[col]) {
            value = value.trim();
            if (value.length === 0) return;
        }

        if (this.inputEditRenamePath) {
            this.inputEditRenamePath.forEach((v) => {
                if (v.key === col)
                    _.set(item, v.path, value);
            });
        }
        if (collapseRow) {
            // collapse the editor panel
            this.expandedElement = null;
        }
        this._announceChange('rename', col);
        this.cd.detectChanges();
    }

    collapseEditor() {
        setTimeout(() => {
            this.expandedElement = null;
            this.cd.detectChanges();
        }, 333);
        // for (let col of this.tableColumn) {
        //     if (col['inputType'] != 'text') {
        //         this._announceChange('rename', col.name);
        //     }
        // }
    }

    dragStarted(e) {
        this.draggingNow = true;
    }

    dragEnded(e) {
        this.draggingNow = false;
    }

    public disableButton(buttonName: string, value: boolean) {
        this._buttonBarComponent.disableButton(buttonName, value);
    }

    public getSelection(): Array<number> {
        const selectedIndexes = [];
        this.highlightedRows.forEach((v) => {
            const found = this.dataSource.data.indexOf(v);
            if (found > -1) selectedIndexes.push(found);
        });
        return selectedIndexes;
    }

    onButtonEditClicked(item, colName) {
        // set value from external dialog assets
        if (!colName) {
            return;
        }
        const event: ITableListEvent = {
            currentAction: 'buttonEdit'
        };
        if (this.callBackEvent) {
            this.callBackEvent(event, (res) => {
                // set value from external input
                let value = res ? res : "";
                if (this.requiredFields && this.requiredFields[colName]) {
                    value = value.trim();
                    if (value.length === 0) return;
                }

                // update value of selected item
                if (this.inputEditRenamePath) {
                    this.inputEditRenamePath.forEach((v) => {
                        if (v.key === colName)
                            _.set(item, v.path, value);
                    });
                }
                this._announceChange('rename', colName);
                this.cd.detectChanges();
            });
        } else {
            this._announceChange('buttonEdit', colName);
        }
    }

    public setSelection(i_selection: Array<number>) {
        this.onDeselect();
        this.highlightedRows = this.dataSource.data.filter((item, index) => {
            return i_selection.indexOf(index) > -1;
        });
        this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
        this._announceChange('externallySelected');
    }

    public setSelectionByIds(i_selection: Array<number>) {
        this.onDeselect();
        this.highlightedRows = this.dataSource.data.filter((item: any, index) => {
            return i_selection.indexOf(item.id) > -1;
        });
        this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
        this._announceChange('externallySelected');
    }

    drop(event: CdkDragDrop<any>) {
        // this.onDeselect();
        // const idx = this.dataSource.data.findIndex((v) => v === event.item.data);
        // moveItemInArray(event.container.data, idx, event.currentIndex);
        // this.dataSource.data[event.currentIndex] = Object.assign({}, this.dataSource.data[event.currentIndex]);

        if (!event) {
            return;
        }
        const pageSize = this.paginator ? this.paginator.pageSize : 0;
        const pageIndex = this.paginator ? this.paginator.pageIndex : 0;
        const prevIndex = pageSize * (pageIndex) + event.previousIndex;
        const currIndex = pageSize * (pageIndex) + event.currentIndex;

        this.clearFilter();
        moveItemInArray(this.dataSource.data, prevIndex, currIndex);
        this.dataSource.data = _.cloneDeep(this.dataSource.data);
        this.dtable.renderRows();
        this._announceChange('drop');

        this.highlightedRows = [this.dataSource.data[currIndex]];
        this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
        this._announceChange('selected');
    }

    applyFilter(filterValue: string) {
        if (!filterValue) filterValue = '';
        if (this.dataSource) {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }

    toggleFilter() {
        this.showFilter = !this.showFilter;
        if (!this.showFilter) this.clearFilter();
    }

    clearFilter() {
        this.filter = '';
        this.applyFilter('');
    }

    applyFilterAbility() {
        if (!this.allowFilter) return;
        // this.dataSource = new MatTableDataSource(sourceStations);
        // this.dataSource.sort = this.sort;

        this.dataSource.filterPredicate = (data: any, filter) => {
            const dataStr = JSON.stringify(data).toLowerCase();
            return dataStr.indexOf(filter) !== -1;
        };
    }

    onDeselect() {
        this.highlightedRows = [];
        this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
        // this._announceChange('deselected');
    }

    onSelectedRow(row: ITableItemInput) {
        if (this.multiSelect) {
            const idx = this.highlightedRows.findIndex((r) => row['__TableId'] == r['__TableId']);
            idx === -1 ? this.highlightedRows.push(row) : this.highlightedRows.splice(this.highlightedRows.indexOf(row), 1);
        } else {
            this.highlightedRows = [row];
        }
        this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
        if (this.expandedElement !== row) {
            this.expandedElement = null;
        }
        this.cd.detectChanges();
        this._announceChange('selected');
    }

    get empty() {
        const {value: {_data}} = this.formGroup;
        return !_data;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent) {
        // if ((event.target as Element).tagName.toLowerCase() != 'input') {
        //     this._elementRef.nativeElement.querySelector('input')!.focus();
        // }
    }

    writeValue(val: any): void {
        this.value = val;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    buttonEmit(name) {
        switch (name) {
            case 'add': {
                if (this.addItemTemplate) {
                    this.dataSource.data.push(_.cloneDeep(this.addItemTemplate));
                    this.value = this.dataSource.data;
                }
                this.onDeselect();
                this._announceChange('added');
                break;
            }
            case 'next': {
                if (this.highlightedRows.length === 0) return;
                let lastIndex = -1;
                this.highlightedRows.forEach((v) => {
                    const found = this.dataSource.data.indexOf(v);
                    if (found > -1) lastIndex = found;
                });
                this.onDeselect();
                if (lastIndex === this.dataSource.data.length - 1) {
                    this.highlightedRows = [this.dataSource.data[0]];
                    this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
                    this._announceChange('selected');
                    return this._announceChange('next');
                }
                this.highlightedRows = [this.dataSource.data[lastIndex + 1]];
                this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
                this._announceChange('selected');
                this._announceChange('next');
                break;
            }
            case 'previous': {
                if (this.highlightedRows.length === 0) return;
                let firstIndex = -1;
                this.highlightedRows.find((v) => {
                    const found = this.dataSource.data.indexOf(v);
                    if (found > -1) firstIndex = found;
                });
                this.onDeselect();
                if (firstIndex === 0) {
                    this.highlightedRows = [this.dataSource.data[this.dataSource.data.length - 1]];
                    this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
                    this._announceChange('selected');
                    return this._announceChange('previous');
                }
                this.highlightedRows = [this.dataSource.data[firstIndex - 1]];
                this.highlightedRowsCopy = _.cloneDeep(this.highlightedRows);
                this._announceChange('selected');
                this._announceChange('previous');
                break;
            }
            case 'remove': {
                const rem = () => {
                    this.dataSource.data = this.dataSource.data.filter((row) => {
                        const idx = this.highlightedRows.findIndex((r) => row['__TableId'] == r['__TableId']);
                        return idx === -1;
                    });
                    this._announceChange('removed');
                    this.onDeselect();
                };
                if (this.highlightedRows.length === 0) return;
                if (this.confirmRemove) {
                    const msg = this.translocoService.translate('general.sureRemove', {}, this.translocoService.getActiveLang());
                    const dialogRef = this.dialog.open(DialogPopComponent, {
                        height: '200px',
                        width: '400px',
                        data: {name: `${msg}`, showCancel: true}
                    });
                    this.cod(
                        dialogRef.afterClosed().subscribe(result => {
                            if (!result) return;
                            rem();
                        })
                    );
                } else {
                    rem();
                }
                break;
            }
            case 'search': {
                break;
            }
            case 'tableOnDialog': {
                break;
            }
            default: {
                this._announceChange(name);
            }
        }
    }

    private _announceChange(name: string, eventField = ''): void {
        const selectedIndexes = [];
        this.highlightedRows.forEach((v) => {
            const found = this.dataSource.data.indexOf(v);
            if (found > -1) selectedIndexes.push(found);
        });
        let d = _.cloneDeep(this.dataSource.data);
        let h = _.cloneDeep(this.highlightedRows);
        if (!this.keepTableIds) {
            d = d.map((item: any, index) => {
                delete item.__TableId;
                if (this.callBackValue) item = this.callBackValue(item, 'OUT', index);
                return item;
            });
            h = h.map((item: any, index) => {
                delete item.__TableId;
                if (this.callBackValue) item = this.callBackValue(item, 'OUT', index);
                return item;
            });
        }
        const r: ITableListEvent = {
            selectedIndexes,
            currentAction: name,
            selected: h,
            values: d,
            eventField
        };

        if (this.callBackEvent) this.callBackEvent(r, null);
        this.stateChanges.next();

        if (!this.disableSubmit) {
            this.update.emit(r);
        }

        this.cd.markForCheck();
    }

    public onAdd(v?) {
    }

    public onRemove() {
    }

    showTableOnDialog() {
        if (!this.isTableOnDialog) {
            this.dialogRef = this.dialog.open(this.tableModal, {
                width: '600px',
                panelClass: 'resizeable-dialog',
            });
            this.isTableOnDialog = true;
            this._buttons.forEach((i_button) => {
                if (i_button.name === 'tableOnDialog') {
                    i_button.icon = 'close';
                }
            });
            this.cod(
                this.dialogRef.afterOpened().subscribe((r) => {
                    // this.dataSource.paginator = this.paginator;
                })
            );
            this.cod(
                this.dialogRef.afterClosed().subscribe((r) => {
                    this.isTableOnDialog = false;
                    // this.dataSource.paginator = this.paginator;
                    this._buttons.forEach((i_button) => {
                        if (i_button.name === 'tableOnDialog') {
                            i_button.icon = 'grid_on';
                        }
                    });
                    this.cd.detectChanges();
                })
            );
        } else {
            if (this.dialogRef) {
                this.dialogRef.close();
            }
        }
        this.cd.detectChanges();
    }

    // Convert decimal color to hex for color picker
    @Memoizee({})
    decimalToHex(decimal: number | string): string {
        if (!decimal && decimal !== 0) return '#000000';
        return Lib.DecimalToHex(decimal);
    }

    // Convert hex color to decimal for storage
    @Memoizee({})
    hexToDecimal(hex: string): number {
        if (!hex) return 0;
        return Lib.HexToDecimal(hex);
    }
}
